// The one CSS import an app may make: the design system (lint rule and consistency test).
import "@companyos/ui/base.css";
// First, before any module builds a zod schema: no `new Function` under the CSP (no unsafe-eval).
import "@companyos/schema/zod-jitless";

import { startThemeSync, toast } from "@companyos/ui";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createApiFromEnv } from "./api/client";
import { createQueryClient } from "./api/query";
import { indexedDbStorage, startChatPersistence } from "./live/persist";
import { startInstallCapture } from "./pwa/install";
import { registerServiceWorker, shouldRegister } from "./pwa/register";
import { App, createAppRouter } from "./router";

// Follow OS theme changes from the start, on every screen (sign-in has no theme control).
startThemeSync();

const api = createApiFromEnv({ VITE_API_URL: import.meta.env.VITE_API_URL as string | undefined });
// The in-memory API's request log, for the browser tests' per-screen call budget (S1-16
// acceptance 3). Only in fake mode: against the real API the tests count network requests.
if (api.fake) {
  (window as unknown as { __companyosFakeCalls?: unknown }).__companyosFakeCalls = api.fake.calls;
  // UI access: what the in-memory API would have mailed (invitation links), for e2e/access.
  (window as unknown as { __companyosFakeMail?: unknown }).__companyosFakeMail = api.fake.mailbox;
  // M4-03: a crew run's proposal (the twin has no run tokens), and the SDK client for a spec's
  // own setup calls (e.g. seating the owner so reminders and role memory have a seat).
  (window as unknown as { __companyosFakeCrewPropose?: unknown }).__companyosFakeCrewPropose =
    api.fake.crewPropose;
  (window as unknown as { __companyosFakeClient?: unknown }).__companyosFakeClient = api.client;
  // HARDEN-pwa: e2e/pwa switches the twin's Web Push on (it has no VAPID keys by default).
  (window as unknown as { __companyosFakePush?: unknown }).__companyosFakePush = (on: boolean) =>
    api.fake?.setPushEnabled(on);
  // SKILLSMITH-HUB: e2e/agent-health files Skillsmith reports (the demo companies have none).
  (window as unknown as { __companyosFakeSkillsmith?: unknown }).__companyosFakeSkillsmith =
    api.fake.fileSkillsmithReport;
  // SKILLSMITH-OPPORTUNITIES: e2e/skillsmith-opportunities files helper opportunities.
  (
    window as unknown as { __companyosFakeSkillsmithOpportunity?: unknown }
  ).__companyosFakeSkillsmithOpportunity = api.fake.fileSkillsmithOpportunity;
  // INBOX-READABLE: the local seed's Recall notices in the owner's inbox (screenshots, e2e).
  (window as unknown as { __companyosFakeMemoryInbox?: unknown }).__companyosFakeMemoryInbox = (
    companyId: string,
  ) => api.fake?.seedMemoryInbox(companyId) ?? 0;
  // INBOX-PAGE-MAIN: the local seed's pending approvals as Waiting on me items (the bell's peek).
  (window as unknown as { __companyosFakeWaitingInbox?: unknown }).__companyosFakeWaitingInbox = (
    companyId: string,
  ) => api.fake?.seedWaitingInbox(companyId) ?? 0;
  // LOOP-BREAKER: e2e/loops puts a caught loop on the page (the demo companies have none).
  (window as unknown as { __companyosFakeLoopIncident?: unknown }).__companyosFakeLoopIncident =
    api.fake.addLoopIncident;
}
const queryClient = createQueryClient(toast);
const context = { api, queryClient };
// CHAT-PERF: Chat's lists survive a reload in IndexedDB (live/persist.ts); quietly off where
// IndexedDB is unavailable, and never for the in-memory fake.
if (api.mode === "http")
  startChatPersistence(queryClient, {
    storage: indexedDbStorage(),
    key: `chat:${api.baseUrl}`,
  });
if (api.fake) {
  // UI access: switch add-ons on, as an operator would; `agents` and `memory` (not sold yet)
  // have no operator switch at all. Two call shapes: e2e/chat (M2-06) passes a list once signed
  // in, so Chat's Agents pieces show; e2e/crew (M4-01) passes one feature and a status before
  // signing in. Either way every demo account that sees the company gets it, and a signed-in
  // session rereads `/v1/me`.
  const fake = api.fake;
  // Both shapes stay callable after the ui/dtp-look merge (M4-05): `(co, feature, status)` is
  // M4-01's switch before signing in (e2e/crew), `(co, features[])` this one (e2e/chat).
  (window as unknown as { __companyosFakeEntitle?: unknown }).__companyosFakeEntitle = async (
    companyId: string,
    features: string | string[],
    status: "active" | "trial" | "grace" | "disabled" = "active",
  ) => {
    for (const f of typeof features === "string" ? [features] : features)
      fake.entitle(companyId, f, status);
    if (fake.signedInAs) await queryClient.invalidateQueries({ queryKey: ["me"] });
  };
}
const router = createAppRouter(context);

// HARDEN-pwa: "Install app" needs the browser's install event from the start (pwa/install.ts);
// the service worker is a production build's, against the real API (pwa/register.ts).
startInstallCapture();
if (
  shouldRegister({
    prod: import.meta.env.PROD,
    fakeApi: Boolean(api.fake),
    flag: import.meta.env.VITE_PWA as string | undefined,
  })
)
  void registerServiceWorker({
    showUpdate: ({ onReload }) =>
      toast.add({
        title: "Update available",
        description: "A new version of CompanyOS is ready.",
        timeout: 0,
        actionProps: { children: "Reload", onClick: onReload },
      }),
  });

const root = document.getElementById("root");
if (!root) throw new Error("#root missing from index.html");

createRoot(root).render(
  <StrictMode>
    <App router={router} context={context} />
  </StrictMode>,
);
